import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import { CloseOutlined, InfoOutlined } from '@mui/icons-material';
import moment from 'moment';
import { formatNumber, dateFormat } from '@utils/format';
import { useAppSelector } from '@store/index';
import { ModalWrapper } from '@hooks/useModal';
import useDisclosure from '@hooks/useDisclosure';
import {
  useCreateContactPersonBillingMutation,
  useLazyGetSubscriptionsQuery,
} from '@api/property.api.rtk';
import { ISubscriptionAuthorizedPerson } from '@pages/subscription/interfaces/form.interface';
import { authorizedPersonSchema } from '@pages/subscription/schema/form.schema';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useToast from '@hooks/useToast';
import { isMobile } from 'react-device-detect';
import { appMode } from '@utils/url';

const ContactPersonBillingContent = ({
  refetch,
  close,
}: {
  refetch: () => void;
  close: () => void;
}) => {
  const [createContactBilling, createContactBillingResponse] =
    useCreateContactPersonBillingMutation();
  const { toast } = useToast();
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<ISubscriptionAuthorizedPerson>({
    resolver: yupResolver(authorizedPersonSchema),
    mode: 'onChange',
  });
  const propertyID = useAppSelector(
    (state) => state.properties.selectedProperty.value,
  );

  const onSubmit = (data: ISubscriptionAuthorizedPerson) => {
    createContactBilling({
      lastName: data.lastName,
      firstName: data.firstName,
      middleName: data.middleName,
      email: data.email,
      contact: data.phoneNumber,
      propertyID,
    });
  };

  useEffect(() => {
    if (createContactBillingResponse.isSuccess) {
      toast({
        severity: 'success',
        message:
          'Successfully created contact billing. You may now pay for your subscription.',
      });
      refetch();
      close();
    }
  }, [createContactBillingResponse.isSuccess, refetch, close, toast]);

  return (
    <Stack component='form' onSubmit={handleSubmit(onSubmit)} gap={2}>
      <Typography noWrap variant='h6'>
        Contact Person Billing
      </Typography>
      {['firstName', 'middleName', 'lastName', 'email', 'phoneNumber'].map(
        (field) => (
          <TextField
            key={field}
            InputLabelProps={{ shrink: true }}
            size='small'
            placeholder={
              field === 'firstName'
                ? 'Juan'
                : field === 'middleName'
                  ? 'D'
                  : field === 'lastName'
                    ? 'Dela Cruz'
                    : field === 'phoneNumber'
                      ? '+639000000000'
                      : field === 'email'
                        ? 'd.delacruz@servoitsolutions.ph'
                        : ''
            }
            label={field
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (str) => str.toUpperCase())}
            type={
              field === 'email'
                ? 'email'
                : field === 'phoneNumber'
                  ? 'tel'
                  : 'text'
            }
            {...register(field as keyof ISubscriptionAuthorizedPerson)}
            error={!!errors?.[field as keyof ISubscriptionAuthorizedPerson]}
            helperText={
              errors?.[field as keyof ISubscriptionAuthorizedPerson]?.message
            }
            fullWidth
          />
        ),
      )}
      <Collapse in={createContactBillingResponse.isError} unmountOnExit>
        <Alert severity='error'>
          {createContactBillingResponse.error + ''}
        </Alert>
      </Collapse>
      <Stack direction='row' justifyContent='flex-end'>
        <Button
          variant='contained'
          type='submit'
          disabled={createContactBillingResponse.isLoading}
        >
          Submit
        </Button>
      </Stack>
    </Stack>
  );
};

const LicenseWarning = () => {
  const properties = useAppSelector((state) => state.properties.properties);
  const selectedProperty = useAppSelector(
    (state) => state.properties.selectedProperty,
  );
  const selectedPropertyDetail = properties.find(
    ({ propertyID }) => propertyID === selectedProperty?.value,
  );
  const isTrial =
    selectedPropertyDetail?.license?.description?.toLowerCase() === 'trial';
  const disclosure = useDisclosure();

  const [showWarning, setShowWarning] = useState(false);

  const [getSubscription, { error, isError, isSuccess, isFetching, data }] =
    useLazyGetSubscriptionsQuery();

  const checkSubscription = useCallback(() => {
    getSubscription({ propertyID: selectedProperty.value });
  }, [getSubscription, selectedProperty.value]);

  const { toast } = useToast();

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  useEffect(() => {
    setShowWarning(
      (Number(selectedPropertyDetail?.license?.expirationDays) || 0) <= 10 &&
        !!selectedPropertyDetail?.propertyID,
    );
  }, [selectedPropertyDetail]);

  const noBillingData =
    isError &&
    'errors' in error &&
    Array.isArray(error?.errors) &&
    error.errors.includes('Kindly build your Billing Information first.');

  const buttonStyle: SxProps = {
    borderColor: 'secondary.contrastText',
    color: 'secondary.contrastText',
    minHeight: 0,
    py: 2 / 8,
    px: { xs: 2, md: 10 / 8 },
    flexShrink: 0,
    ':hover': {
      bgcolor: 'secondary.contrastText',
      color: 'secondary.main',
      borderColor: 'secondary.contrastText',
    },
  };

  const handleButtonClick = () => {
    if (noBillingData) {
      disclosure.onOpen();
      return;
    }

    if (!isSuccess) {
      toast({
        severity: 'error',
        message: 'Failed to fetch subscription data.',
      });
      return;
    }

    window.location.href = data?.subscription?.linking_url;
  };

  return (
    <>
      <Collapse
        in={
          showWarning &&
          // !import.meta.env.DEV &&
          ['production', 'uat', 'development'].includes(appMode)
        }
        unmountOnExit
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{
            bgcolor: 'secondary.main',
            height: { xs: 'auto', md: 50 },
            width: '100vw',
            overflowX: 'auto',
          }}
        >
          <Box />
          <Stack direction='row'>
            <Collapse in={isFetching} orientation='horizontal' unmountOnExit>
              <CircularProgress
                sx={{ color: 'secondary.contrastText' }}
                size={20}
              />
            </Collapse>
            <Collapse
              in={!isFetching && noBillingData}
              orientation='horizontal'
              unmountOnExit
            >
              <Stack
                direction='row'
                alignItems='center'
                gap={1}
                sx={{ color: 'secondary.contrastText', py: 1 }}
              >
                <InfoOutlined />
                {selectedPropertyDetail && (
                  <Typography
                    fontSize={{ xs: 12, md: 14 }}
                    sx={{ textWrap: { xs: 'wrap', md: 'nowrap' } }}
                  >
                    {isError && 'message' in error && error.message}.
                  </Typography>
                )}
                <Button
                  variant='outlined'
                  sx={buttonStyle}
                  onClick={handleButtonClick}
                >
                  <Typography noWrap>Enter Billing Data</Typography>
                </Button>
              </Stack>
            </Collapse>
            <Collapse
              in={!isFetching && !noBillingData}
              orientation='horizontal'
              unmountOnExit
            >
              <Stack
                direction='row'
                alignItems='stretch'
                justifyContent='center'
                gap={1}
                sx={{
                  color: 'secondary.contrastText',
                  p: { xs: 1, md: 0 },
                  py: 1,
                }}
              >
                <Stack direction='row' alignItems='center' gap={1}>
                  <InfoOutlined />
                  {selectedPropertyDetail &&
                    (isTrial ? (
                      <Typography
                        fontSize={{ xs: 12, md: 14 }}
                        sx={{ textWrap: { xs: 'wrap', md: 'nowrap' } }}
                      >
                        Your trial period will end in{' '}
                        <b>
                          {formatNumber(
                            selectedPropertyDetail?.license?.expirationDays ||
                              0,
                          )}{' '}
                          {selectedPropertyDetail?.license?.expirationDays === 1
                            ? 'day'
                            : 'days'}
                        </b>
                        . Please pay for the license on or before{' '}
                        <b>
                          {moment(
                            selectedPropertyDetail?.license?.expirationDate,
                          ).format(dateFormat)}
                        </b>{' '}
                        to continue.
                      </Typography>
                    ) : (
                      <Typography
                        fontSize={{ xs: 12, md: 14 }}
                        sx={{ textWrap: { xs: 'wrap', md: 'nowrap' } }}
                      >
                        You have{' '}
                        <b>
                          {formatNumber(
                            selectedPropertyDetail?.license?.expirationDays ||
                              0,
                          )}{' '}
                          {selectedPropertyDetail?.license?.expirationDays === 1
                            ? 'day'
                            : 'days'}
                        </b>{' '}
                        left to renew your license. Please complete the renewal
                        before{' '}
                        <b>
                          {moment(
                            selectedPropertyDetail?.license?.expirationDate,
                          ).format(dateFormat)}
                        </b>
                        .
                      </Typography>
                    ))}
                </Stack>
                <Button
                  variant='outlined'
                  sx={buttonStyle}
                  onClick={handleButtonClick}
                >
                  <Typography noWrap>
                    {isTrial ? 'Go Live' : 'Renew'}
                  </Typography>
                </Button>
              </Stack>
            </Collapse>
          </Stack>
          {!isMobile && (
            <IconButton
              sx={{
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
              }}
              onClick={() => setShowWarning(false)}
            >
              <CloseOutlined />
            </IconButton>
          )}
        </Stack>
      </Collapse>
      <ModalWrapper modalDisclosure={disclosure}>
        <Card sx={{ p: 2, minWidth: 420 }}>
          <Stack direction='row' justifyContent='flex-end'>
            <IconButton onClick={() => disclosure.onClose()}>
              <CloseOutlined />
            </IconButton>
          </Stack>
          <ContactPersonBillingContent
            refetch={checkSubscription}
            close={() => disclosure.onClose()}
          />
        </Card>
      </ModalWrapper>
    </>
  );
};

export default LicenseWarning;
